import Select, { MultiValue, StylesConfig } from 'react-select';

import { useGetWalletTagsQuery } from '@entities/wallet-tags';
import { reactFormSelectMultiStyles } from '@shared/components/Form';
import { TTableColumnsFilter, useTableContext } from '@shared/components/Table';

import { ITagOption, tagToOption } from './WalletTagsSelect';

const baseStyles = reactFormSelectMultiStyles as StylesConfig<ITagOption, true>;

// Chips-in-control multi-select (same family as the Policies "Currencies" SelectMulti). Chips wrap
// onto several lines and are kept compact; font weight is forced normal (the header th is 600).
const tagFilterStyles: StylesConfig<ITagOption, true> = {
  ...baseStyles,
  control: (provided, state) => ({
    ...(baseStyles.control?.(provided, state) ?? provided),
    fontWeight: 'normal',
  }),
  placeholder: provided => ({
    ...provided,
    fontWeight: 'normal',
    textAlign: 'left',
    color: '#757575',
  }),
  menu: provided => ({
    ...provided,
    fontWeight: 'normal',
  }),
  option: (provided, state) => ({
    ...(baseStyles.option?.(provided, state) ?? provided),
    textAlign: 'left',
  }),
  multiValue: base => ({
    ...base,
    margin: '1px 2px',
  }),
  multiValueLabel: base => ({
    ...base,
    padding: '0 2px',
    paddingLeft: 5,
    fontSize: 11,
  }),
  multiValueRemove: base => ({
    ...base,
    paddingLeft: 2,
    paddingRight: 3,
  }),
};

interface IProps {
  /** Filter param name; defaults to `tags` → emits the array `filter[tags][]=<id>`. */
  name?: string;
}

/**
 * Multi-select, searchable tag filter for the wallets table. Renders selected tags as compact chips
 * in the control (wrapping onto several lines), reusing the wallet-tags source. Selection is derived
 * from the current filter (single source of truth), so it stays in sync with the URL and resets with
 * the rest of the table filters.
 */
export const WalletTagsFilter = <T,>({ name = 'tags' }: IProps) => {
  const { currentFilter, updateFilter, deleteFilterKey } = useTableContext<T>();
  const filterKey = `filter[${name}][]`;

  const { data, isLoading } = useGetWalletTagsQuery();
  const options: ITagOption[] = (data?.data ?? []).map(tagToOption);
  const optionsByValue = new Map(options.map(option => [option.value, option]));

  const selected = currentFilter
    .getAll(filterKey)
    .map(value => optionsByValue.get(value))
    .filter((option): option is ITagOption => Boolean(option));

  const handleChange = (next: MultiValue<ITagOption>) => {
    if (next.length === 0) {
      deleteFilterKey(filterKey);
      return;
    }

    const newFilter = {} as Record<string, string>;
    next.forEach((option, index) => {
      newFilter[`${filterKey}[${index}]`] = option.value;
    });
    updateFilter(newFilter as TTableColumnsFilter<T>);
  };

  return (
    <Select
      isMulti
      isClearable
      isLoading={isLoading}
      closeMenuOnSelect={false}
      options={options}
      value={selected}
      onChange={handleChange}
      styles={tagFilterStyles}
      className='wallet-tags-filter'
      classNamePrefix='select'
      maxMenuHeight={320}
      menuPosition='fixed'
      placeholder='Search'
    />
  );
};
